import React, {useCallback, useContext, useEffect, useReducer, useState} from 'react';
import {BaseStyle} from "../../../../../styles/shared/BaseStyle";
import {useHistory, useParams} from "react-router-dom";
import {
	Backdrop,
	Card,
	CardActions,
	CardContent,
	CardHeader,
	CircularProgress,
	Container,
	Typography
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import HideAppBar from "../../../../widgets/header/HideAppBar";
import {useForm, FormProvider} from "react-hook-form";
import {
	initQuaranteneState,
	QUARANTENE_INITIAL_STATE,
	QuaranteneReducer
} from "../../../../../reducers/QuaranteneReducer";
import {calcPunteggioIsolamento, MonitoraggioIsolamentoGrid} from "./MonitoraggioIsolamentoGrid";
import {
	generateMonitoraggio,
	getQuarantenaById,
} from "../../../../../services/QuaranteneServices";
import {AnagraficaContext, AppContext} from "../../../../../App";
import {setOpenSnackbar} from "../../../../../services/AppServices";
import { Alert, AlertTitle, Skeleton } from '@mui/material';
import {calcPunteggioQuarantenaBoolean, MonitoraggioQuarantenaGrid} from "./MonitoraggioQuarantenaGrid";
import {getAnagraficaById} from "../../../../../services/AnagraficaServices";
import {capitalizeWords} from "../../../../../utils/GenericUtils";
import {useTheme} from "@mui/styles";
import {getMonitoraggioApi} from "../../../../../services/utils/MonitoraggioApiData";

const FormMonitoraggio = () => {
	const classes = BaseStyle();
	let history = useHistory();
	const appContext = useContext(AppContext);
	const anagraficaContext = useContext(AnagraficaContext);
	let theme = useTheme();
	const [quarantenaState, quarantenaDispatch] = useReducer(QuaranteneReducer, QUARANTENE_INITIAL_STATE, initQuaranteneState);
	const methods = useForm({mode: "onChange"});
	let {id, idSoggetto} = useParams();
	const [submit, setSubmit] = useState(false);
	
	const onSubmit = useCallback((data) => {
		let allOk = true;
		if (data["febbre"] && (parseFloat(data["febbre"]) > 40 || parseFloat(data["febbre"]) < 33)) {
			allOk = false;
			setOpenSnackbar("warning", "Controlla di aver inserito dei valori corretti per la febbre!", appContext.dispatch);
		}
		if (data["saturazioneO2"] && (parseFloat(data["saturazioneO2"]) > 100 || parseFloat(data["saturazioneO2"]) < 80)) {
			allOk = false;
			setOpenSnackbar("warning", "Controlla di aver inserito dei valori corretti per la saturazione!", appContext.dispatch);
		}
		if (data["pressioneArteriosaMax"] && (parseFloat(data["pressioneArteriosaMax"]) > 180 || parseFloat(data["pressioneArteriosaMax"]) < 130)) {
			allOk = false;
			setOpenSnackbar("warning", "Controlla di aver inserito dei valori corretti per la pressione arteriosa massima!", appContext.dispatch);
		}
		if (allOk) {
			setSubmit(true);
			let score;
			if (quarantenaState.quarantena?.tipo?.includes("Isolamento")) {
				score = calcPunteggioIsolamento(data);
			} else {
				let intermediateScore = calcPunteggioQuarantenaBoolean(data);
				score = intermediateScore * 4 / 12;
			}
			generateMonitoraggio(getMonitoraggioApi({
				...data,
				score: score
			}, id, idSoggetto, quarantenaState.quarantena?.tipo), quarantenaDispatch);
		}
	}, [id, quarantenaDispatch, appContext.dispatch, idSoggetto, quarantenaState.quarantena])
	
	useEffect(() => {
		if (submit && !quarantenaState.loadingGenerateMonitoraggio && quarantenaState.errorGenerateMonitoraggio === "") {
			setSubmit(false);
			history.goBack();
		} else if (submit && !quarantenaState.loadingGenerateMonitoraggio && quarantenaState.errorGenerateMonitoraggio !== "") {
			setSubmit(false);
			setOpenSnackbar("error", quarantenaState.errorGenerateMonitoraggio, appContext.dispatch);
		}
	}, [submit, history, appContext.dispatch, quarantenaState.loadingGenerateMonitoraggio, quarantenaState.errorGenerateMonitoraggio])
	
	useEffect(() => {
		getQuarantenaById(id, quarantenaDispatch);
	}, [id, quarantenaDispatch])
	
	useEffect(() => {
		getAnagraficaById(idSoggetto, anagraficaContext.dispatch);
	}, [idSoggetto, anagraficaContext.dispatch])
	
	return (
		<React.Fragment>
			<Backdrop open={submit} className={classes.backdrop}>
				<CircularProgress/>
			</Backdrop>
			<main className={classes.main}>
				<HideAppBar title={"Monitoraggio paziente"} back={true}/>
				<Container maxWidth={"xl"} component={"article"} style={{paddingTop: 12}}>
					<Grid container spacing={3}>
						<Grid item xs={12}>
							<Card className={classes.roundBorder}
							      style={{height: "100%", display: "flex", flexDirection: "column"}}>
								<CardHeader
									title={<Typography
										variant={"h6"}><b>Monitoraggio manuale tramite Callcenter</b>
										<br/>Paziente:
										{
											anagraficaContext.state.loadingGetAnagrafica ?
												<Skeleton/> : anagraficaContext.state.errorGetAnagrafica !== "" ? "Err.." :
													<span
														style={{
															fontSize: 30,
															color: theme.palette.primary.main,
															fontWeight: "bold"
														}}
													>{" " + capitalizeWords(anagraficaContext.state.anagrafica.nome) + " " + capitalizeWords(anagraficaContext.state.anagrafica.cognome)}</span>
										}
									</Typography>}
									subheader={"Compila il form indicando le risposte solo se il paziente te le fornisce, in caso contrario " +
										"mantieni la risposta come \"Non dichiarato\" in modo da far pesare meno una risposta non dichiarata rispetto ad una dichiarazione dell'utente."}
								/>
								<CardContent style={{flexGrow: 1}}>
									<FormProvider {...methods}>
										<form onSubmit={methods.handleSubmit(onSubmit)}>
											{
												quarantenaState.loadingGetQuarantena ? <Skeleton/> :
													quarantenaState.errorGetQuarantena !== "" ?
														<Alert severity="error" style={{borderRadius: 24}}>
															<AlertTitle>Errore</AlertTitle>
															Prova ad effettuare un refresh della pagina entro 5 minuti. Se l'errore persiste contatta
															gli sviluppatori.
														</Alert> :
														quarantenaState.quarantena?.tipo?.includes("Isolamento") ?
															<MonitoraggioIsolamentoGrid/> : <MonitoraggioQuarantenaGrid/>
											}
										</form>
									</FormProvider>
								</CardContent>
								<CardActions className={classes.flexRight}>
									<Tooltip
										title={"Conferma i dati indicati nel form"}>
										<Button variant={"contained"} disableElevation color="primary" disabled={submit}
										        style={{borderRadius: 24}}
										        className={classes.roundButton} onClick={methods.handleSubmit(onSubmit)}>
											<b>Conferma ed invia</b>
										</Button>
									</Tooltip>
								</CardActions>
							</Card>
						</Grid>
					</Grid>
				</Container>
			</main>
		</React.Fragment>
	);
};

export default React.memo(FormMonitoraggio);
